import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  IconButton,
  Chip,
  Avatar,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  useGetAdminCourseAdsQuery,
  useDeleteCourseAdMutation,
} from "@/store/api/homepageApi";
import CourseAdForm from "./CourseAdForm";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";

const CourseAdsList = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formDialog, setFormDialog] = useState({ open: false, courseAd: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, courseAd: null });

  const { data, isLoading, refetch } = useGetAdminCourseAdsQuery({
    page: page + 1,
    limit: rowsPerPage,
  });

  const [deleteCourseAd, { isLoading: isDeleting }] = useDeleteCourseAdMutation();

  const courseAds = data?.data || [];
  const pagination = data?.pagination || { total: 0, totalPages: 0 };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async () => {
    try {
      await deleteCourseAd(deleteDialog.courseAd._id).unwrap();
      toast.success("Course ad deleted successfully");
      setDeleteDialog({ open: false, courseAd: null });
      refetch();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to delete course ad");
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Course Ads Management
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setFormDialog({ open: true, courseAd: null })}
          >
            Add Course Ad
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Cover</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : courseAds.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <Typography variant="body1" color="text.secondary">
                    No course ads found. Click "Add Course Ad" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              courseAds.map((courseAd) => (
                <TableRow key={courseAd._id} hover>
                  <TableCell>
                    {courseAd.coverImage ? (
                      <Avatar
                        src={courseAd.coverImage}
                        alt={courseAd.title}
                        variant="rounded"
                        sx={{ width: 60, height: 80 }}
                      />
                    ) : (
                      <Avatar variant="rounded" sx={{ width: 60, height: 80 }}>
                        📚
                      </Avatar>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {courseAd.title}
                    </Typography>
                    {courseAd.sampleVideoUrl && (
                      <Chip
                        label="Has Video"
                        size="small"
                        color="primary"
                        sx={{ mt: 0.5, height: 20, fontSize: "0.7rem" }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 250,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {courseAd.description || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {courseAd.price === null || courseAd.price === undefined ? (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    ) : courseAd.price === 0 ? (
                      <Typography variant="body2" sx={{ color: "#4CAF50", fontWeight: 600 }}>Free</Typography>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {courseAd.price.toLocaleString("en-US")} ₫
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={courseAd.isActive ? "Active" : "Inactive"}
                      size="small"
                      color={courseAd.isActive ? "success" : "default"}
                      icon={
                        courseAd.isActive ? (
                          <VisibilityIcon />
                        ) : (
                          <VisibilityOffIcon />
                        )
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() =>
                          setFormDialog({ open: true, courseAd })
                        }
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          setDeleteDialog({ open: true, courseAd })
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={pagination.total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      <CourseAdForm
        open={formDialog.open}
        courseAd={formDialog.courseAd}
        onClose={() => setFormDialog({ open: false, courseAd: null })}
        onSuccess={() => refetch()}
      />

      <ConfirmDialog
        open={deleteDialog.open}
        title="Delete Course Ad"
        message={`Are you sure you want to delete "${deleteDialog.courseAd?.title}"? This action cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialog({ open: false, courseAd: null })}
        confirmText="Delete"
        confirmColor="error"
        isLoading={isDeleting}
      />
    </Box>
  );
};

export default CourseAdsList;
